import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ErrorBanner, useAsync } from '../../components/Async'
import { PageTitle } from '../../components/PageTitle'
import { PRODUCT_NAME } from '../../lib/product'
import {
  ApiError,
  catalogs,
  dictionaries,
  policies,
  violationMessages,
  type BuilderCatalog,
  type DictionarySummary,
  type ViolationMessage
} from '../../lib/api'
import { previewNotice } from '../violationMessages/violationMessages'
import {
  agentKeysFor,
  asksForMessage,
  defaultGroupName,
  findAssistant,
  hostsFor,
  normalizeBuilder,
  parseBuilder,
  previousStep,
  reachSentence,
  recipeAvailability,
  recipesFor,
  serializeBuilder,
  summarySentence,
  type BuilderState,
  type BuilderStep,
  type HostKind
} from './policyBuilder'

/**
 * The policy builder: what to watch, where it runs, and what to do about it (ADR-S-052).
 *
 * Three questions an operator can answer without knowing what a capability, a surface or a selector
 * is, and a fourth - what the person is told - for a recipe that tells them anything (ADR-S-053). The answers become one policy per assistant host, because that is what the agent accepts, and
 * the group page that follows shows them as the one decision they were. Everything the old form could
 * set is still reachable from there, under Advanced settings, and "Start from scratch" goes straight
 * to it for somebody who wants the form.
 *
 * Every choice is in the URL, and moving forward pushes a history entry, so the browser's back button
 * steps back through the wizard rather than leaving it.
 */
export function PolicyBuilderPage() {
  const catalog = useAsync(() => catalogs.builder(), [])

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>Build a policy</PageTitle>
          <p>Choose what to watch, where it runs, and what to do about it.</p>
        </div>
        <Link className="button" to="/policies/new" data-testid="start-from-scratch">
          Start from scratch instead
        </Link>
      </div>

      <ErrorBanner message={catalog.error} />
      {catalog.value ? (
        <Builder catalog={catalog.value} />
      ) : (
        !catalog.error && (
          <div className="card">
            <div className="card-body">
              <div className="empty">Loading the builder…</div>
            </div>
          </div>
        )
      )}
    </>
  )
}

const STEP_TITLES: [BuilderStep, string][] = [
  ['what', 'What to watch'],
  ['host', 'Where it runs'],
  ['recipe', 'What to do'],
  ['message', "What they're told"]
]

function Builder({ catalog }: { catalog: BuilderCatalog }) {
  const location = useLocation()
  const navigate = useNavigate()
  const state = useMemo(() => normalizeBuilder(parseBuilder(location.search), catalog), [location.search, catalog])
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const go = (changes: Partial<BuilderState>) => {
    const next = normalizeBuilder({ ...state, ...changes }, catalog)
    navigate({ search: serializeBuilder(location.search, next) })
  }

  const back = previousStep(catalog, state)
  const steps = STEP_TITLES.filter(
    ([step]) => (step !== 'host' || state.target !== 'computer') && (step !== 'message' || asksForMessage(catalog, state))
  )

  const create = async () => {
    if (!state.recipe) return
    setBusy(true)
    setFailure(null)
    try {
      const created = await policies.fromBuilder({
        recipeKey: state.recipe,
        assistantKey: state.target === 'assistant' ? state.assistant : null,
        agentKeys: state.target === 'assistant' ? agentKeysFor(catalog, state.assistant, state.hosts) : [],
        screenshotOnDetection: state.screenshot,
        name: name.trim() || undefined,
        dictionaryIds: state.dictionaries,
        violationMessageId: state.message
      })
      navigate(`/policies/groups/${created.groupId}`, { state: { created: true, warnings: created.warnings } })
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The policies could not be created.')
      setBusy(false)
    }
  }

  return (
    <div data-testid="policy-builder">
      <ol className="builder-steps" aria-label="Steps">
        {steps.map(([step, title], index) => (
          <li key={step} aria-current={state.step === step ? 'step' : undefined} data-testid={`builder-progress-${step}`}>
            <span className="builder-step-number">{index + 1}</span> {title}
          </li>
        ))}
      </ol>

      <ErrorBanner message={failure} />

      {state.step === 'what' && <WhatStep catalog={catalog} state={state} go={go} />}
      {state.step === 'host' && <HostStep catalog={catalog} state={state} go={go} />}
      {state.step === 'recipe' && (
        <RecipeStep catalog={catalog} state={state} go={go} name={name} onName={setName} busy={busy} onCreate={() => void create()} />
      )}
      {state.step === 'message' && (
        <MessageStep catalog={catalog} state={state} go={go} name={name} onName={setName} busy={busy} onCreate={() => void create()} />
      )}

      {back && (
        <div className="toolbar" style={{ marginTop: '1rem' }}>
          <button type="button" onClick={() => go({ step: back })} data-testid="builder-back">
            Back
          </button>
        </div>
      )}
    </div>
  )
}

interface StepProps {
  catalog: BuilderCatalog
  state: BuilderState
  go: (changes: Partial<BuilderState>) => void
}

function WhatStep({ catalog, state, go }: StepProps) {
  return (
    <div className="card" data-testid="builder-step-what">
      <div className="card-body">
        <h2 className="builder-question">What do you want to watch?</h2>

        <h3>An AI assistant</h3>
        <ul className="choice-grid">
          {catalog.assistants.map((assistant) => (
            <li key={assistant.key}>
              <button
                type="button"
                className="choice-card"
                aria-pressed={state.target === 'assistant' && state.assistant === assistant.key}
                onClick={() =>
                  go({
                    target: 'assistant',
                    assistant: assistant.key,
                    hosts: assistant.key === state.assistant ? state.hosts : [],
                    step: assistant.hosts.length > 1 ? 'host' : 'recipe'
                  })
                }
                data-testid={`assistant-${assistant.key}`}
              >
                <span className="choice-title">{assistant.displayName}</span>
                <span className="hint">{assistant.hosts.map((host) => host.hostLabel).join(' · ')}</span>
              </button>
            </li>
          ))}
        </ul>

        <p className="hint" role="note" data-testid="prompt-observation-note">
          {catalog.promptObservationNote}{' '}
          {reachSentence(catalog.promptObservationReach) && (
            <strong data-testid="prompt-observation-reach">{reachSentence(catalog.promptObservationReach)}</strong>
          )}
        </p>

        <h3>The whole computer</h3>
        <ul className="choice-grid">
          <li>
            <button
              type="button"
              className="choice-card"
              aria-pressed={state.target === 'computer'}
              onClick={() => go({ target: 'computer', step: 'recipe' })}
              data-testid="whole-computer"
            >
              <span className="choice-title">Something on the whole computer</span>
              <span className="hint">Which applications are open, or a daily screenshot. Runs on every agent.</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  )
}

function HostStep({ catalog, state, go }: StepProps) {
  const assistant = findAssistant(catalog, state.assistant)!
  const toggle = (host: HostKind, on: boolean) =>
    go({ hosts: on ? [...state.hosts, host] : state.hosts.filter((candidate) => candidate !== host) })

  return (
    <div className="card" data-testid="builder-step-host">
      <div className="card-body">
        <h2 className="builder-question">Where does {assistant.displayName} run?</h2>
        <p className="hint">
          Choose every place you want covered. Each one becomes its own policy, and they stay together as
          one group.
        </p>
        <ul className="choice-grid">
          {assistant.hosts.map((host) => (
            <li key={host.host}>
              <label className="choice-card choice-check">
                <input
                  type="checkbox"
                  checked={state.hosts.includes(host.host)}
                  onChange={(event) => toggle(host.host, event.target.checked)}
                  data-testid={`host-${host.host}`}
                />
                <span>
                  <span className="choice-title">{host.hostLabel}</span>
                  <span className="hint">{host.surfaceDescription}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
        <div className="toolbar" style={{ marginTop: '1rem' }}>
          <button
            type="button"
            className="primary"
            disabled={state.hosts.length === 0}
            onClick={() => go({ step: 'recipe' })}
            data-testid="host-continue"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  )
}

interface RecipeStepProps extends StepProps {
  name: string
  onName: (name: string) => void
  busy: boolean
  onCreate: () => void
}

function RecipeStep({ catalog, state, go, name, onName, busy, onCreate }: RecipeStepProps) {
  const agentKeys = agentKeysFor(catalog, state.assistant, state.hosts)
  const recipes = recipesFor(catalog, state.target)
  const chosen = recipes.find((recipe) => recipe.key === state.recipe)
  const notes = chosen ? recipeAvailability(catalog, chosen, agentKeys).notes : []
  const assistant = findAssistant(catalog, state.assistant)

  return (
    <div className="card" data-testid="builder-step-recipe">
      <div className="card-body">
        <h2 className="builder-question">
          {assistant
            ? `What should happen in ${assistant.displayName} ${hostPhrase(catalog, state)}?`
            : 'What should happen on the computer?'}
        </h2>

        <ul className="choice-grid choice-grid-wide" role="radiogroup" aria-label="Policy">
          {recipes.map((recipe) => {
            const availability = recipeAvailability(catalog, recipe, agentKeys)
            const selected = state.recipe === recipe.key
            return (
              <li key={recipe.key}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className="choice-card"
                  disabled={!availability.enabled}
                  onClick={() => go({ recipe: recipe.key })}
                  data-testid={`recipe-${recipe.key}`}
                >
                  <span className="choice-title">{recipe.label}</span>
                  <span className="hint">{recipe.explanation}</span>
                  <span className="hint">
                    {recipe.triggerSummary}
                    {recipe.sendsPromptText ? ' · Sends the matching prompt text' : ''}
                  </span>
                  {!availability.enabled && availability.reasons.length > 0 && (
                    <span className="choice-reason" data-testid={`recipe-${recipe.key}-unavailable`}>
                      {availability.reasons.map((reason) => reason.reason).join(' ')}
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>

        {chosen && (
          <div className="builder-details" data-testid="builder-details">
            {notes.map((note) => (
              <div key={note} className="banner warning" role="note">
                {note}
              </div>
            ))}

            {chosen.needsDictionary && (
              <DictionaryChooser chosen={state.dictionaries} onChange={(next) => go({ dictionaries: next })} />
            )}

            {state.target === 'assistant' && (
              <div className="field">
                <label className="inline-check">
                  <input
                    type="checkbox"
                    checked={state.screenshot}
                    onChange={(event) => go({ screenshot: event.target.checked })}
                    data-testid="builder-screenshot"
                  />
                  <span>{catalog.screenshotOnDetection.label}</span>
                </label>
                <div className="hint">{catalog.screenshotOnDetection.explanation}</div>
              </div>
            )}

            {chosen.notifies ? (
              <div className="toolbar">
                <button type="button" className="primary" onClick={() => go({ step: 'message' })} data-testid="builder-next">
                  Next: what they're told
                </button>
              </div>
            ) : (
              <CreateSection catalog={catalog} state={state} name={name} onName={onName} busy={busy} onCreate={onCreate} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** The name, the sentence that sums the choices up, and the button that makes them. The last thing on whichever step is last. */
function CreateSection({ catalog, state, name, onName, busy, onCreate }: Omit<RecipeStepProps, 'go'>) {
  const summary = summarySentence(catalog, state)
  return (
    <>
      <div className="field">
        <label htmlFor="builder-name">Name</label>
        <input
          id="builder-name"
          value={name}
          onChange={(event) => onName(event.target.value)}
          placeholder={defaultGroupName(catalog, state)}
          maxLength={128}
          data-testid="builder-name"
        />
        <div className="hint">Leave it empty to use the name shown.</div>
      </div>

      {summary && (
        <p className="lede" data-testid="builder-summary">
          {summary}
        </p>
      )}

      <div className="toolbar">
        <button type="button" className="primary" disabled={busy} onClick={onCreate} data-testid="builder-create">
          Create as drafts
        </button>
      </div>
      <p className="hint">Nothing reaches a device until the drafts are approved and published on the next page.</p>
    </>
  )
}

function hostPhrase(catalog: BuilderCatalog, state: BuilderState): string {
  const labels = hostsFor(catalog, state.assistant)
    .filter((host) => state.hosts.includes(host.host))
    .map((host) => host.hostLabel.toLowerCase())
  if (labels.length <= 1) return labels[0] ?? ''
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/**
 * Which dictionaries the new policies will bind.
 *
 * Chosen before anything exists, unlike the editor's binding panel, which records against a saved
 * policy. The choice travels in the URL and is bound by the server in the same transaction that creates
 * the policies. A dictionary with no published revision can be chosen, and the banner says that
 * publishing will wait for it, the same refusal the editor describes.
 */
function DictionaryChooser({ chosen, onChange }: { chosen: string[]; onChange: (next: string[]) => void }) {
  const [search, setSearch] = useState('')
  const [matches, setMatches] = useState<DictionarySummary[] | null>(null)
  const [total, setTotal] = useState(0)
  const [selected, setSelected] = useState<DictionarySummary[]>([])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      dictionaries
        .list({ search: search || undefined, limit: 15 })
        .then((page) => {
          setMatches(page.items)
          setTotal(page.total)
        })
        .catch(() => setMatches([]))
    }, 250)
    return () => window.clearTimeout(timer)
  }, [search])

  // The chosen set is read by identity, so a dictionary chosen from an earlier search, or carried in a
  // pasted link, still shows by name.
  useEffect(() => {
    if (chosen.length === 0) {
      setSelected([])
      return
    }
    dictionaries
      .list({ ids: chosen, limit: chosen.length })
      .then((page) => setSelected(page.items))
      .catch(() => setSelected([]))
  }, [chosen])

  const toggle = (dictionaryId: string, on: boolean) =>
    onChange(on ? [...chosen, dictionaryId] : chosen.filter((candidate) => candidate !== dictionaryId))

  const shown = [...selected, ...(matches ?? []).filter((entry) => !chosen.includes(entry.dictionaryId))]
  const unpublished = selected.filter((entry) => (entry.publishedRevision ?? null) === null)

  return (
    <fieldset className="field builder-dictionaries" data-testid="builder-dictionaries">
      <legend>What it looks for</legend>
      <p className="hint">
        Choose the dictionaries whose words count as sensitive. Every organization starts with the
        collections {PRODUCT_NAME} ships, and you can edit them or add your own on the Dictionaries tab.
      </p>
      <input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search dictionaries"
        aria-label="Search dictionaries"
        data-testid="builder-dictionary-search"
      />
      <ul className="choice-list">
        {shown.map((entry) => (
          <li key={entry.dictionaryId}>
            <label className="inline-check">
              <input
                type="checkbox"
                checked={chosen.includes(entry.dictionaryId)}
                onChange={(event) => toggle(entry.dictionaryId, event.target.checked)}
                data-testid={`builder-dictionary-${entry.dictionaryId}`}
              />
              <span>
                {entry.name}
                <span className="muted">
                  {' '}
                  · {entry.contentRuleCount} {entry.contentRuleCount === 1 ? 'rule' : 'rules'}
                  {(entry.publishedRevision ?? null) === null ? ' · not published' : ''}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      {matches !== null && total > (matches?.length ?? 0) && (
        <p className="hint">
          Showing {matches.length} of {total}. Search to narrow them.
        </p>
      )}
      {chosen.length === 0 && (
        <p className="hint" data-testid="builder-no-dictionary">
          With none chosen, the policy has nothing to match until a dictionary is bound.
        </p>
      )}
      {unpublished.length > 0 && (
        <div className="banner warning" role="note">
          {unpublished.length === 1
            ? `'${unpublished[0]!.name}' has no published revision,`
            : `${unpublished.length} chosen dictionaries have no published revision,`}{' '}
          so publishing these policies waits until it is published.
        </div>
      )}
    </fieldset>
  )
}

/**
 * What the person is told when a policy stops them (ADR-S-053): the builder's fourth step, asked only
 * for a recipe that tells them anything.
 *
 * Every message is shown as it would appear, because the point of choosing is the words, and a list of
 * names hides them. The organization's own come first: a message citing the handbook is the reason the
 * library exists. The server attaches the choice to the policies that read prompts and to nothing else.
 */
function MessageStep({ catalog, state, go, name, onName, busy, onCreate }: RecipeStepProps) {
  const [messages, setMessages] = useState<ViolationMessage[] | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    violationMessages
      .list()
      .then((list) => setMessages(list.items))
      .catch((error: unknown) => setFailure(error instanceof ApiError ? error.message : 'The messages could not be read.'))
  }, [])

  const ordered = messages ? [...messages.filter((message) => !message.builtIn), ...messages.filter((message) => message.builtIn)] : []

  return (
    <div className="card" data-testid="builder-step-message">
      <div className="card-body">
        <h2 className="builder-question">What should the person be told?</h2>
        <p className="hint">
          Cite the practice in your handbook that was broken. Write your own on the{' '}
          <Link to="/policies?tab=messages">Messages</Link> tab; it is offered here the next time.
        </p>
        <ErrorBanner message={failure} />

        <ul className="choice-grid choice-grid-wide" role="radiogroup" aria-label="Violation message">
          <li>
            <button
              type="button"
              role="radio"
              aria-checked={state.message === null}
              className="choice-card"
              onClick={() => go({ message: null })}
              data-testid="builder-message-own"
            >
              <span className="choice-title">The recipe's own words</span>
              <span className="hint">What the recipe says today. You can change it later under Advanced settings.</span>
            </button>
          </li>
          {ordered.map((message) => (
            <li key={message.violationMessageId}>
              <button
                type="button"
                role="radio"
                aria-checked={state.message === message.violationMessageId}
                className="choice-card"
                onClick={() => go({ message: message.violationMessageId })}
                data-testid={`builder-message-${message.violationMessageId}`}
              >
                <span className="choice-title">
                  {message.name}
                  {!message.builtIn && message.reference ? ` (${message.reference})` : ''}
                </span>
                <span className="notice-preview" aria-label="What the person sees">
                  <strong>{previewNotice(message.title, message.reference ?? '')}</strong>
                  <span>{previewNotice(message.message, message.reference ?? '')}</span>
                </span>
                <span className="hint">{message.builtIn ? 'Built in' : "Your organization's"}</span>
              </button>
            </li>
          ))}
        </ul>
        {messages === null && !failure && <div className="empty">Loading the messages…</div>}

        <div className="builder-details">
          <CreateSection catalog={catalog} state={state} name={name} onName={onName} busy={busy} onCreate={onCreate} />
        </div>
      </div>
    </div>
  )
}
